import { useEffect, useState } from "react";
import { Gift, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import Leaderboard from "@/components/Leaderboard";

export default function ShareAstra({ className = "" }) {
  const [ref, setRef] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/referral").then(({ data }) => setRef(data)).catch(() => {});
  }, []);

  const share = async () => {
    if (!ref) return;
    setBusy(true);
    const text = `I've been reading my daily stars on Astra. Join with my link and we both get ${ref.bonus} free questions for Astra Interactive.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Astra – your daily horoscope", text, url: ref.link });
      } else {
        await navigator.clipboard.writeText(`${text} ${ref.link}`);
        toast("Invite link copied", { description: "Paste it into a message to a friend." });
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        await navigator.clipboard?.writeText(ref.link).catch(() => {});
        toast("Invite link copied", { description: ref.link });
      }
    }
    setBusy(false);
  };

  return (
    <div data-testid="share-astra-card" className={`bg-[#0F1523] border border-[#D4AF37]/30 rounded-2xl p-5 ${className}`}>
      <div className="flex items-center gap-2 mb-1">
        <Gift size={17} className="gold-text" />
        <p className="text-lg">Recommend a friend</p>
      </div>
      <p className="text-sm text-[#A7B0C0] mb-3">
        When a friend joins with your link, you both get {ref?.bonus ?? 3} free Astra Interactive questions. Invite{" "}
        {ref?.premium_at ?? 5} friends and you also get <span className="gold-text">a free month of Premium</span>.
      </p>
      {ref && <InviteProgress r={ref} />}
      <button
        data-testid="btn-share-astra"
        onClick={share}
        disabled={!ref || busy}
        className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3 rounded-full transition-colors disabled:opacity-60"
      >
        {busy || !ref ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
        Share Astra
      </button>
      <Leaderboard data={ref?.leaderboard} />
    </div>
  );
}

function InviteProgress({ r }) {
  const n = Math.min(r.count, r.premium_at);
  return (
    <div data-testid="invite-progress" className="mb-4">
      <div className="flex justify-between text-xs text-[#A7B0C0] mb-1.5">
        <span data-testid="referral-count">{r.count} friend{r.count === 1 ? "" : "s"} joined</span>
        <span className="gold-text">
          {r.premium_awarded ? "Free Premium month unlocked" : `${r.premium_at - n} more for a free Premium month`}
        </span>
      </div>
      <div className="flex gap-1.5">
        {Array.from({ length: r.premium_at }).map((_, i) => (
          <span key={i} className={`h-1.5 flex-1 rounded-full ${i < n ? "bg-[#D4AF37]" : "bg-[#D4AF37]/15"}`} />
        ))}
      </div>
    </div>
  );
}
